import React, { useState } from 'react';
import { formatCurrency } from '../../utils/currencyFormatter';
import { BarChart2Icon, FilterIcon, CheckIcon, AlertTriangleIcon, XIcon, DownloadIcon } from 'lucide-react';
export function UsageTracking({
  locale
}) {
  // State for tracking filters
  const [filters, setFilters] = useState({
    entity: 'all',
    site: 'all',
    metric: 'all',
    period: '30'
  });
  // Mock usage data
  const usageData = [{
    id: 'usage-1',
    metricName: 'Documents',
    entityName: 'Global Technologies Inc.',
    siteName: 'New York HQ',
    usageCount: 75420,
    limit: 100000,
    lastUpdated: '2023-10-22T14:30:00Z',
    status: 'Near Limit'
  }, {
    id: 'usage-2',
    metricName: 'Invoices',
    entityName: 'Global Technologies Inc.',
    siteName: 'New York HQ',
    usageCount: 22456,
    limit: 50000,
    lastUpdated: '2023-10-22T14:30:00Z',
    status: 'OK'
  }, {
    id: 'usage-3',
    metricName: 'Vendors',
    entityName: 'Global Technologies Inc.',
    siteName: 'New York HQ',
    usageCount: 342,
    limit: 500,
    lastUpdated: '2023-10-22T14:30:00Z',
    status: 'OK'
  }, {
    id: 'usage-4',
    metricName: 'Invoices',
    entityName: 'European Operations Ltd',
    siteName: 'All Sites',
    usageCount: 48500,
    limit: 50000,
    lastUpdated: '2023-10-22T14:30:00Z',
    status: 'Near Limit'
  }, {
    id: 'usage-5',
    metricName: 'Users',
    entityName: 'Global Technologies Inc.',
    siteName: 'All Sites',
    usageCount: 48,
    limit: 50,
    lastUpdated: '2023-10-22T14:30:00Z',
    status: 'Near Limit'
  }, {
    id: 'usage-6',
    metricName: 'Data Points',
    entityName: 'Global Technologies Inc.',
    siteName: 'All Sites',
    usageCount: 262500,
    limit: 250000,
    lastUpdated: '2023-10-22T14:30:00Z',
    status: 'Exceeded'
  }];
  // Daily usage data for chart
  const dailyUsageData = [{
    date: '2023-10-01',
    documents: 2100,
    invoices: 750,
    vendors: 10
  }, {
    date: '2023-10-02',
    documents: 2300,
    invoices: 800,
    vendors: 12
  }, {
    date: '2023-10-03',
    documents: 2200,
    invoices: 780,
    vendors: 8
  }, {
    date: '2023-10-04',
    documents: 2400,
    invoices: 820,
    vendors: 15
  }, {
    date: '2023-10-05',
    documents: 2500,
    invoices: 850,
    vendors: 9
  }, {
    date: '2023-10-06',
    documents: 2350,
    invoices: 790,
    vendors: 11
  }, {
    date: '2023-10-07',
    documents: 2150,
    invoices: 760,
    vendors: 7
  }, {
    date: '2023-10-08',
    documents: 2600,
    invoices: 880,
    vendors: 14
  }, {
    date: '2023-10-09',
    documents: 2700,
    invoices: 900,
    vendors: 16
  }, {
    date: '2023-10-10',
    documents: 2800,
    invoices: 950,
    vendors: 13
  }, {
    date: '2023-10-11',
    documents: 2750,
    invoices: 920,
    vendors: 10
  }, {
    date: '2023-10-12',
    documents: 2650,
    invoices: 890,
    vendors: 9
  }, {
    date: '2023-10-13',
    documents: 2900,
    invoices: 980,
    vendors: 17
  }, {
    date: '2023-10-14',
    documents: 3000,
    invoices: 1000,
    vendors: 15
  }];
  // Handle filter changes
  const handleFilterChange = e => {
    const {
      name,
      value
    } = e.target;
    setFilters({
      ...filters,
      [name]: value
    });
  };
  // Filter the usage data based on selected filters
  const filteredData = usageData.filter(item => {
    return (filters.entity === 'all' || item.entityName === filters.entity) && (filters.site === 'all' || item.siteName === filters.site) && (filters.metric === 'all' || item.metricName === filters.metric);
  });
  // Get status badge styling
  const getStatusBadge = status => {
    switch (status) {
      case 'OK':
        return {
          bg: 'bg-green-100',
          text: 'text-green-800',
          icon: <CheckIcon className="h-4 w-4 mr-1" data-id="element-18219" />
        };
      case 'Near Limit':
        return {
          bg: 'bg-yellow-100',
          text: 'text-yellow-800',
          icon: <AlertTriangleIcon className="h-4 w-4 mr-1" data-id="element-18220" />
        };
      case 'Exceeded':
        return {
          bg: 'bg-red-100',
          text: 'text-red-800',
          icon: <XIcon className="h-4 w-4 mr-1" data-id="element-18221" />
        };
      default:
        return {
          bg: 'bg-gray-100',
          text: 'text-gray-800',
          icon: null
        };
    }
  };
  return <div className="space-y-6" data-id="element-18222">
      <div className="flex justify-between items-center" data-id="element-18223">
        <div data-id="element-18224">
          <h3 className="text-lg font-medium text-gray-900" data-id="element-18225">Usage Tracking</h3>
          <p className="mt-1 text-sm text-gray-500" data-id="element-18226">
            Monitor your system usage across entities, sites, and metrics
          </p>
        </div>
        <button type="button" className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-18227">
          <DownloadIcon className="h-4 w-4 mr-2" data-id="element-18228" />
          Export Report
        </button>
      </div>
      {/* Filters */}
      <div className="bg-white shadow px-4 py-5 sm:rounded-lg sm:p-6" data-id="element-18229">
        <div className="md:grid md:grid-cols-4 md:gap-6" data-id="element-18230">
          <div className="md:col-span-1" data-id="element-18231">
            <h3 className="text-lg font-medium leading-6 text-gray-900" data-id="element-18232">
              Filters
            </h3>
            <p className="mt-1 text-sm text-gray-500" data-id="element-18233">
              Filter usage data by entity, site, metric and time period
            </p>
          </div>
          <div className="mt-5 md:mt-0 md:col-span-3" data-id="element-18234">
            <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" data-id="element-18235">
              <div className="sm:col-span-3" data-id="element-18236">
                <label htmlFor="entity" className="block text-sm font-medium text-gray-700" data-id="element-18237">
                  Entity
                </label>
                <div className="mt-1" data-id="element-18238">
                  <select id="entity" name="entity" value={filters.entity} onChange={handleFilterChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-18239">
                    <option value="all" data-id="element-18240">All Entities</option>
                    <option value="Global Technologies Inc." data-id="element-18241">
                      Global Technologies Inc.
                    </option>
                    <option value="European Operations Ltd" data-id="element-18242">
                      European Operations Ltd
                    </option>
                  </select>
                </div>
              </div>
              <div className="sm:col-span-3" data-id="element-18243">
                <label htmlFor="site" className="block text-sm font-medium text-gray-700" data-id="element-18244">
                  Site
                </label>
                <div className="mt-1" data-id="element-18245">
                  <select id="site" name="site" value={filters.site} onChange={handleFilterChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-18246">
                    <option value="all" data-id="element-18247">All Sites</option>
                    <option value="New York HQ" data-id="element-18248">New York HQ</option>
                    <option value="San Francisco Office" data-id="element-18249">
                      San Francisco Office
                    </option>
                  </select>
                </div>
              </div>
              <div className="sm:col-span-3" data-id="element-18250">
                <label htmlFor="metric" className="block text-sm font-medium text-gray-700" data-id="element-18251">
                  Metric
                </label>
                <div className="mt-1" data-id="element-18252">
                  <select id="metric" name="metric" value={filters.metric} onChange={handleFilterChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-18253">
                    <option value="all" data-id="element-18254">All Metrics</option>
                    <option value="Documents" data-id="element-18255">Documents</option>
                    <option value="Invoices" data-id="element-18256">Invoices</option>
                    <option value="Vendors" data-id="element-18257">Vendors</option>
                    <option value="Users" data-id="element-18258">Users</option>
                    <option value="Data Points" data-id="element-18259">Data Points</option>
                  </select>
                </div>
              </div>
              <div className="sm:col-span-3" data-id="element-18260">
                <label htmlFor="period" className="block text-sm font-medium text-gray-700" data-id="element-18261">
                  Time Period
                </label>
                <div className="mt-1" data-id="element-18262">
                  <select id="period" name="period" value={filters.period} onChange={handleFilterChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-18263">
                    <option value="7" data-id="element-18264">Last 7 days</option>
                    <option value="30" data-id="element-18265">Last 30 days</option>
                    <option value="90" data-id="element-18266">Last 90 days</option>
                    <option value="365" data-id="element-18267">Last year</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Usage Chart */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-18268">
        <div className="px-4 py-5 sm:px-6 flex justify-between items-center" data-id="element-18269">
          <div data-id="element-18270">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-18271">
              Usage Trend
            </h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500" data-id="element-18272">
              Daily usage trend for the selected time period
            </p>
          </div>
        </div>
        <div className="border-t border-gray-200 px-4 py-5 sm:p-6" data-id="element-18273">
          <div className="h-64 bg-gray-50 rounded-lg flex items-center justify-center" data-id="element-18274">
            <div className="text-center" data-id="element-18275">
              <BarChart2Icon className="mx-auto h-12 w-12 text-gray-400" data-id="element-18276" />
              <h3 className="mt-2 text-sm font-medium text-gray-900" data-id="element-18277">
                Usage chart visualization
              </h3>
              <p className="mt-1 text-sm text-gray-500" data-id="element-18278">
                This would display a chart of usage trends over time
              </p>
            </div>
          </div>
        </div>
      </div>
      {/* Usage Table */}
      <div className="flex flex-col" data-id="element-18279">
        <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8" data-id="element-18280">
          <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8" data-id="element-18281">
            <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg" data-id="element-18282">
              <table className="min-w-full divide-y divide-gray-200" data-id="element-18283">
                <thead className="bg-gray-50" data-id="element-18284">
                  <tr data-id="element-18285">
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-18286">
                      Metric
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-18287">
                      Entity / Site
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-18288">
                      Usage
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-18289">
                      Last Updated
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-18290">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-18291">
                  {filteredData.map(item => {
                  const statusBadge = getStatusBadge(item.status);
                  const usagePercentage = Math.round(item.usageCount / item.limit * 100);
                  return <tr key={item.id} data-id="element-18292">
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-18293">
                          <div className="text-sm font-medium text-gray-900" data-id="element-18294">
                            {item.metricName}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-18295">
                          <div className="text-sm text-gray-900" data-id="element-18296">
                            {item.entityName}
                          </div>
                          <div className="text-sm text-gray-500" data-id="element-18297">
                            {item.siteName}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-18298">
                          <div className="text-sm text-gray-900" data-id="element-18299">
                            {item.usageCount.toLocaleString()} /{' '}
                            {item.limit.toLocaleString()}
                          </div>
                          <div className="w-full h-2 bg-gray-200 rounded-full mt-2" data-id="element-18300">
                            <div className={`h-full rounded-full ${usagePercentage >= 90 ? 'bg-red-500' : usagePercentage >= 75 ? 'bg-yellow-500' : 'bg-green-500'}`} style={{
                          width: `${usagePercentage}%`
                        }} data-id="element-18301"></div>
                          </div>
                          <div className="text-xs text-gray-500 mt-1" data-id="element-18302">
                            {usagePercentage}% used
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-18303">
                          <div className="text-sm text-gray-900" data-id="element-18304">
                            {new Date(item.lastUpdated).toLocaleDateString(locale)}
                          </div>
                          <div className="text-sm text-gray-500" data-id="element-18305">
                            {new Date(item.lastUpdated).toLocaleTimeString(locale)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-18306">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusBadge.bg} ${statusBadge.text}`} data-id="element-18307">
                            {statusBadge.icon}
                            {item.status}
                          </span>
                        </td>
                      </tr>;
                })}
                </tbody>
              </table>
              {filteredData.length === 0 && <div className="px-6 py-4 text-center text-sm text-gray-500" data-id="element-18308">
                  No usage data found for the selected filters.
                </div>}
            </div>
          </div>
        </div>
      </div>
    </div>;
}